// frontend/src/pages/BoardPacks.jsx
// Module 3: Board Pack Generator
// Week 5 — homepage feature: generate PDF board paper from live data,
// view pack history, upload signed attestation
// commit: feat(w5): add BoardPacks page with generate, history, and attestation upload

import React, { useState, useEffect, useRef } from 'react';
import {
  getBoardPacks,
  generateBoardPack,
  uploadAttestation,
  deleteBoardPack,
  downloadBoardPack,
} from '../api';

// ─── Status helpers ───────────────────────────────────────────────────────────
function AttestationBadge({ pack }) {
  if (pack.attested_at) {
    return (
      <span style={{
        display: 'inline-block', padding: '2px 10px', borderRadius: 3,
        fontSize: 11, fontWeight: 700,
        background: '#d4edda', color: '#155724', border: '1px solid #c3e6cb',
      }}>
        Attested
      </span>
    );
  }
  if (pack.attestation_required) {
    return (
      <span style={{
        display: 'inline-block', padding: '2px 10px', borderRadius: 3,
        fontSize: 11, fontWeight: 700,
        background: '#fff3cd', color: '#856404', border: '1px solid #ffeeba',
      }}>
        Awaiting Attestation
      </span>
    );
  }
  return (
    <span style={{
      display: 'inline-block', padding: '2px 10px', borderRadius: 3,
      fontSize: 11, fontWeight: 700,
      background: '#e2e3e5', color: '#383d41',
    }}>
      Not Required
    </span>
  );
}

function fmtDatetime(d) {
  if (!d) return '—';
  return new Date(d).toLocaleString('en-AU', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
}

// ─── Generate modal ───────────────────────────────────────────────────────────
function GenerateModal({ onClose, onGenerated }) {
  const today     = new Date().toISOString().split('T')[0];
  const firstOfMonth = new Date(new Date().getFullYear(), new Date().getMonth(), 1)
    .toISOString().split('T')[0];

  const [form, setForm] = useState({
    period_label: '',
    period_start: firstOfMonth,
    period_end:   today,
  });
  const [generating, setGenerating] = useState(false);
  const [error, setError]           = useState('');

  function set(field, value) {
    setForm(prev => ({ ...prev, [field]: value }));
  }

  async function handleGenerate() {
    if (!form.period_label.trim()) {
      setError('Period label is required.');
      return;
    }
    if (!form.period_start || !form.period_end) {
      setError('Both period start and end dates are required.');
      return;
    }
    setError('');
    setGenerating(true);
    try {
      const pack = await generateBoardPack(form);
      onGenerated(pack);
    } catch (err) {
      setError(err.message || 'Generation failed. Please try again.');
      setGenerating(false);
    }
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000,
    }}>
      <div style={{
        background: '#fff', borderRadius: 8, padding: '36px 40px',
        width: 480, boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
      }}>
        <div style={{ fontSize: 18, fontWeight: 700, color: '#132e50', marginBottom: 6 }}>
          Generate Board Pack
        </div>
        <div style={{ fontSize: 12, color: '#6b6b6b', marginBottom: 28, lineHeight: 1.5 }}>
          The pack will be built from live platform data at the moment you click Generate.
          Controls, vendors, and open incidents are captured as a point-in-time snapshot.
        </div>

        {error && (
          <div style={{
            background: '#f8d7da', border: '1px solid #f5c6cb',
            borderRadius: 5, padding: '10px 14px',
            fontSize: 12, color: '#721c24', marginBottom: 20,
          }}>
            {error}
          </div>
        )}

        <div style={{ marginBottom: 20 }}>
          <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#132e50', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.3 }}>
            Period Label *
          </label>
          <input
            type="text"
            placeholder="e.g. Q2 FY2026 — April to June 2026"
            value={form.period_label}
            onChange={e => set('period_label', e.target.value)}
            style={{
              width: '100%', padding: '9px 12px', borderRadius: 5,
              border: '1px solid #d0ccc6', fontSize: 13, color: '#132e50',
              outline: 'none', fontFamily: 'inherit',
            }}
          />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 28 }}>
          <div>
            <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#132e50', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.3 }}>
              Period Start *
            </label>
            <input
              type="date"
              value={form.period_start}
              onChange={e => set('period_start', e.target.value)}
              style={{
                width: '100%', padding: '9px 12px', borderRadius: 5,
                border: '1px solid #d0ccc6', fontSize: 13, color: '#132e50',
                outline: 'none', fontFamily: 'inherit',
              }}
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#132e50', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.3 }}>
              Period End *
            </label>
            <input
              type="date"
              value={form.period_end}
              onChange={e => set('period_end', e.target.value)}
              style={{
                width: '100%', padding: '9px 12px', borderRadius: 5,
                border: '1px solid #d0ccc6', fontSize: 13, color: '#132e50',
                outline: 'none', fontFamily: 'inherit',
              }}
            />
          </div>
        </div>

        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
          <button
            onClick={onClose}
            disabled={generating}
            style={{
              padding: '9px 20px', borderRadius: 5, border: '1px solid #d0ccc6',
              background: '#fff', color: '#5a5a5a', fontSize: 13, fontWeight: 600,
              cursor: generating ? 'not-allowed' : 'pointer', fontFamily: 'inherit',
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleGenerate}
            disabled={generating}
            style={{
              padding: '9px 24px', borderRadius: 5, border: 'none',
              background: generating ? '#6b85a3' : '#132e50',
              color: '#fff', fontSize: 13, fontWeight: 700,
              cursor: generating ? 'not-allowed' : 'pointer', fontFamily: 'inherit',
              display: 'flex', alignItems: 'center', gap: 8,
            }}
          >
            {generating && (
              <span style={{
                width: 14, height: 14, border: '2px solid rgba(255,255,255,0.3)',
                borderTopColor: '#fff', borderRadius: '50%',
                display: 'inline-block', animation: 'spin 0.8s linear infinite',
              }} />
            )}
            {generating ? 'Generating PDF…' : 'Generate'}
          </button>
        </div>
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

// ─── Attestation upload panel ─────────────────────────────────────────────────
function AttestationPanel({ pack, onAttested, canAttest }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError]         = useState('');

  async function handleFileChange(e) {
    const file = e.target.files[0];
    if (!file) return;
    setError('');
    setUploading(true);
    try {
      const result = await uploadAttestation(pack.id, file);
      onAttested(pack.id, result.attested_at);
    } catch (err) {
      setError(err.message || 'Upload failed.');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  }

  if (pack.attested_at) {
    return (
      <div style={{ fontSize: 11, color: '#155724' }}>
        Attested {fmtDatetime(pack.attested_at)}
        {pack.attested_by_name && ` by ${pack.attested_by_name}`}
      </div>
    );
  }

  // Only a board member or an administrator can attest a pack. The server
  // also refuses the user who generated the pack, and shows its message here.
  if (!canAttest) {
    return (
      <div style={{ fontSize: 11, color: '#666' }}>
        Awaiting attestation by a board member or administrator.
      </div>
    );
  }

  return (
    <div>
      {error && (
        <div style={{ fontSize: 11, color: '#721c24', marginBottom: 6 }}>{error}</div>
      )}
      <label style={{
        display: 'inline-block', padding: '6px 14px', borderRadius: 5,
        border: '1px solid #b8860b', background: '#fff',
        color: '#b8860b', fontSize: 11, fontWeight: 700,
        cursor: uploading ? 'not-allowed' : 'pointer',
      }}>
        {uploading ? 'Uploading…' : 'Upload Signed Attestation'}
        <input
          ref={fileRef}
          type="file"
          accept=".pdf,.png,.jpg,.jpeg"
          style={{ display: 'none' }}
          disabled={uploading}
          onChange={handleFileChange}
        />
      </label>
      <div style={{ fontSize: 10, color: '#8a8a8a', marginTop: 4 }}>
        PDF, PNG, or JPG — max 10 MB
      </div>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────
export default function BoardPacks({ user }) {
  const [packs, setPacks]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [showModal, setShowModal] = useState(false);

  const canGenerate = ['admin', 'compliance_manager'].includes(user?.role);
  const canAttest   = ['admin', 'board_member'].includes(user?.role);

  useEffect(() => {
    loadPacks();
  }, []);

  async function loadPacks() {
    setLoading(true);
    setError('');
    try {
      const data = await getBoardPacks();
      setPacks(data);
    } catch (err) {
      setError('Failed to load board packs.');
    } finally {
      setLoading(false);
    }
  }

  function handleGenerated(newPack) {
    setShowModal(false);
    // Reload to get the full record with generated_by_name etc.
    loadPacks();
    // Start the download immediately
    handleDownload(newPack);
  }

  async function handleDownload(pack) {
    try {
      await downloadBoardPack(pack.id);
    } catch (err) {
      alert(err.message || 'Download failed.');
    }
  }

  function handleAttested(packId, attestedAt) {
    setPacks(prev => prev.map(p =>
      p.id === packId ? { ...p, attested_at: attestedAt } : p
    ));
  }

  async function handleDelete(pack) {
    if (!window.confirm(`Delete board pack "${pack.period_label}"? This cannot be undone.`)) return;
    try {
      await deleteBoardPack(pack.id);
      setPacks(prev => prev.filter(p => p.id !== pack.id));
    } catch (err) {
      alert(err.message || 'Delete failed.');
    }
  }

  // ─── Summary stats ─────────────────────────────────────────────────────────
  const totalPacks    = packs.length;
  const attested      = packs.filter(p => p.attested_at).length;
  const awaitingAttest = packs.filter(p => !p.attested_at && p.attestation_required).length;

  return (
    <div style={{ padding: '40px 48px', maxWidth: 1200 }}>

      {/* Page header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 32 }}>
        <div>
          <h1 style={{ fontSize: 26, fontWeight: 700, color: '#132e50', margin: 0, marginBottom: 6 }}>
            Board Pack Generator
          </h1>
          <p style={{ fontSize: 13, color: '#6b6b6b', margin: 0, lineHeight: 1.5 }}>
            Generate a board-grade AI governance report from live platform data.
            Each pack captures a point-in-time snapshot of controls, vendor risk, and open incidents.
          </p>
        </div>
        {canGenerate && (
          <button
            onClick={() => setShowModal(true)}
            style={{
              padding: '10px 22px', borderRadius: 6, border: 'none',
              background: '#132e50', color: '#fff',
              fontSize: 13, fontWeight: 700, cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: 8,
              flexShrink: 0, marginLeft: 24,
              fontFamily: 'inherit',
            }}
          >
            <span style={{ fontSize: 16 }}>⊕</span>
            Generate Board Pack
          </button>
        )}
      </div>

      {/* Summary cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 32 }}>
        {[
          { label: 'Total Packs', value: totalPacks, sub: 'generated', color: '#132e50' },
          { label: 'Attested', value: attested, sub: 'with signed attestation', color: '#155724' },
          { label: 'Awaiting Attestation', value: awaitingAttest, sub: 'require board sign-off', color: awaitingAttest > 0 ? '#856404' : '#132e50' },
        ].map(card => (
          <div key={card.label} style={{
            background: '#fff', border: '1px solid #e8e4de', borderRadius: 8,
            padding: '20px 24px', borderTop: `3px solid ${card.color}`,
          }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: '#8a8a8a', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>
              {card.label}
            </div>
            <div style={{ fontSize: 32, fontWeight: 700, color: card.color, lineHeight: 1 }}>{card.value}</div>
            <div style={{ fontSize: 11, color: '#8a8a8a', marginTop: 4 }}>{card.sub}</div>
          </div>
        ))}
      </div>

      {/* Error state */}
      {error && (
        <div style={{
          background: '#f8d7da', border: '1px solid #f5c6cb',
          borderRadius: 6, padding: '14px 18px',
          fontSize: 13, color: '#721c24', marginBottom: 24,
        }}>
          {error}
        </div>
      )}

      {/* Pack list */}
      <div style={{ background: '#fff', border: '1px solid #e8e4de', borderRadius: 8, overflow: 'hidden' }}>

        {/* Table header */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr auto',
          gap: 0,
          background: '#132e50',
          padding: '12px 20px',
        }}>
          {['Period', 'Generated', 'Generated By', 'Status', 'Attestation', ''].map((h, i) => (
            <div key={i} style={{
              fontSize: 10, fontWeight: 700,
              color: i === 0 ? '#b8860b' : '#c8d6e8',
              textTransform: 'uppercase', letterSpacing: 0.5,
              paddingRight: 12,
            }}>
              {h}
            </div>
          ))}
        </div>

        {/* Loading */}
        {loading && (
          <div style={{ padding: '40px 20px', textAlign: 'center', color: '#8a8a8a', fontSize: 13 }}>
            Loading board packs…
          </div>
        )}

        {/* Empty */}
        {!loading && packs.length === 0 && (
          <div style={{ padding: '56px 20px', textAlign: 'center' }}>
            <div style={{ fontSize: 32, marginBottom: 12 }}>⊕</div>
            <div style={{ fontSize: 14, fontWeight: 600, color: '#132e50', marginBottom: 6 }}>
              No board packs generated yet
            </div>
            <div style={{ fontSize: 12, color: '#8a8a8a', marginBottom: 20 }}>
              Generate your first board pack to produce a board-grade governance report
              from live platform data.
            </div>
            {canGenerate && (
              <button
                onClick={() => setShowModal(true)}
                style={{
                  padding: '9px 20px', borderRadius: 5, border: 'none',
                  background: '#132e50', color: '#fff',
                  fontSize: 12, fontWeight: 700, cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                Generate Board Pack
              </button>
            )}
          </div>
        )}

        {/* Rows */}
        {!loading && packs.map((pack, idx) => (
          <div
            key={pack.id}
            style={{
              display: 'grid',
              gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr auto',
              gap: 0,
              padding: '16px 20px',
              borderBottom: idx < packs.length - 1 ? '1px solid #f0ece6' : 'none',
              alignItems: 'center',
              background: idx % 2 === 0 ? '#fff' : '#fdfcfb',
            }}
          >
            {/* Period */}
            <div style={{ paddingRight: 12 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#132e50', marginBottom: 2 }}>
                {pack.period_label}
              </div>
              <div style={{ fontSize: 11, color: '#8a8a8a' }}>
                Pack ID: <span style={{ fontFamily: 'monospace', fontSize: 10 }}>{pack.id.slice(0, 8)}…</span>
              </div>
            </div>

            {/* Generated at */}
            <div style={{ fontSize: 12, color: '#5a5a5a', paddingRight: 12 }}>
              {fmtDatetime(pack.generated_at)}
            </div>

            {/* Generated by */}
            <div style={{ fontSize: 12, color: '#5a5a5a', paddingRight: 12 }}>
              {pack.generated_by_name || '—'}
            </div>

            {/* Attestation status badge */}
            <div style={{ paddingRight: 12 }}>
              <AttestationBadge pack={pack} />
            </div>

            {/* Attestation action or date */}
            <div style={{ paddingRight: 12 }}>
              <AttestationPanel
                pack={pack}
                onAttested={handleAttested}
                canAttest={canAttest}
              />
            </div>

            {/* Actions */}
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              {pack.pdf_path && (
                <button
                  type="button"
                  onClick={() => handleDownload(pack)}
                  style={{
                    padding: '6px 14px', borderRadius: 5,
                    border: '1px solid #132e50', background: '#fff',
                    color: '#132e50', fontSize: 11, fontWeight: 700,
                    whiteSpace: 'nowrap', cursor: 'pointer', fontFamily: 'inherit',
                  }}
                >
                  Download PDF
                </button>
              )}
              {user?.role === 'admin' && (
                <button
                  onClick={() => handleDelete(pack)}
                  style={{
                    padding: '6px 10px', borderRadius: 5,
                    border: '1px solid #f5c6cb', background: '#fff',
                    color: '#721c24', fontSize: 11, fontWeight: 600,
                    cursor: 'pointer', fontFamily: 'inherit',
                  }}
                >
                  Delete
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Regulatory context note */}
      <div style={{
        marginTop: 24, background: '#f8f6f2',
        borderLeft: '3px solid #b8860b',
        padding: '14px 20px', borderRadius: '0 4px 4px 0',
        fontSize: 11, color: '#5a5a5a', lineHeight: 1.6,
      }}>
        Board packs support attestation obligations under the Financial Accountability and Remuneration Act 2023 (FAR),
        APRA CPS 230, and APRA CPS 234. Each pack stores a point-in-time snapshot of governance data as audit evidence.
        Signed attestations are retained as part of the audit log.
      </div>

      {/* Generate modal */}
      {showModal && (
        <GenerateModal
          onClose={() => setShowModal(false)}
          onGenerated={handleGenerated}
        />
      )}
    </div>
  );
}
